import { supabase } from './supabase';

// Get upcoming events that need reminders (within X days)
export const getUpcomingEventsForReminders = async (daysBefore = 7) => {
  const today = new Date();
  const reminderDate = new Date();
  reminderDate.setDate(today.getDate() + daysBefore);
  
  const { data, error } = await supabase
    .from('events')
    .select(`
      *,
      event_registrations (
        user_id,
        registration_type,
        status
      )
    `)
    .gte('start_date', today.toISOString().split('T')[0])
    .lte('start_date', reminderDate.toISOString().split('T')[0])
    .in('status', ['upcoming', 'ongoing'])
    .order('start_date', { ascending: true });
  
  if (error) throw error;
  return data || [];
};

// Get registered attendees for an event
export const getEventAttendees = async (eventId) => {
  const { data, error } = await supabase
    .from('event_registrations')
    .select(`
      user_id,
      registration_type,
      status,
      profiles (
        email,
        full_name
      )
    `)
    .eq('event_id', eventId)
    .eq('status', 'registered');
  
  if (error) throw error;
  return data;
};

// Log event reminder sent
export const logEventReminder = async (eventId, recipientEmail, reminderType) => {
  const { data, error } = await supabase
    .from('event_reminders')
    .insert({
      event_id: eventId,
      recipient_email: recipientEmail,
      reminder_type: reminderType,
      sent_at: new Date().toISOString()
    });
  
  return { data, error };
};

// Check if event reminder was sent recently
export const wasEventReminderSent = async (eventId, recipientEmail, days = 7) => {
  const cutoffDate = new Date();
  cutoffDate.setDate(cutoffDate.getDate() - days);
  
  const { data, error } = await supabase
    .from('event_reminders')
    .select('id')
    .eq('event_id', eventId)
    .eq('recipient_email', recipientEmail)
    .gte('sent_at', cutoffDate.toISOString())
    .limit(1);
  
  if (error) return false;
  return data && data.length > 0;
};

// Generate event reminder email content
export const generateEventReminderEmail = (event, attendee) => {
  const eventDate = new Date(event.start_date);
  const formattedDate = eventDate.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  
  const daysUntil = Math.ceil((eventDate - new Date()) / (1000 * 60 * 60 * 24));
  
  return {
    subject: `Reminder: ${event.title} is in ${daysUntil} days!`,
    body: `
Hello ${attendee.profiles?.full_name || 'Attendee'},

This is a friendly reminder that you're registered for ${event.title}!

📅 Date: ${formattedDate}
📍 Location: ${event.venue_name || event.location || 'TBA'}
${event.registration_url ? '\n🔗 Event Page: ' + event.registration_url : ''}

${daysUntil === 1 ? 
  'The event is tomorrow!' : 
  `The event is in ${daysUntil} days.`
}

${event.description ? 
  '\nEvent Details:\n' + event.description : 
  ''
}

Need to make changes to your registration?
Visit the event page: ${typeof window !== 'undefined' ? window.location.origin : 'https://confplanners.com'}/events/${event.id}

We look forward to seeing you there!

Best regards,
The ConfPlanners Team
    `.trim()
  };
};

// Generate event reminder HTML email
export const generateEventReminderHtml = (event, attendee) => {
  const eventDate = new Date(event.start_date);
  const formattedDate = eventDate.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  
  const daysUntil = Math.ceil((eventDate - new Date()) / (1000 * 60 * 60 * 24));
  
  return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
</head>
<body style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; line-height: 1.6; color: #1f2937; max-width: 600px; margin: 0 auto; padding: 20px;">
  <div style="background: linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%); padding: 30px; border-radius: 12px 12px 0 0; text-align: center;">
    <h1 style="color: white; margin: 0; font-size: 24px;">ConfPlanners</h1>
    <p style="color: white; margin: 10px 0 0; opacity: 0.9;">Event Reminder</p>
  </div>
  
  <div style="background: #ffffff; padding: 30px; border: 1px solid #e5e7eb; border-top: none; border-radius: 0 0 12px 12px;">
    <h2 style="color: #1f2937; margin-top: 0;">Hello ${attendee.profiles?.full_name || 'there'}!</h2>
    
    <p style="color: #4b5563;">This is a friendly reminder that you're registered for:</p>
    
    <div style="background: #f3f4f6; padding: 20px; border-radius: 8px; margin: 20px 0;">
      <h3 style="color: #1f2937; margin-top: 0; font-size: 20px;">${event.title}</h3>
      <p style="color: #4b5563; margin: 10px 0;">
        <strong>📅 Date:</strong> ${formattedDate}<br>
        <strong>📍 Location:</strong> ${event.venue_name || event.location || 'TBA'}
      </p>
    </div>
    
    <div style="background: #fef3c7; padding: 20px; border-radius: 8px; margin: 20px 0; border-left: 4px solid #f59e0b;">
      <p style="color: #92400e; margin: 0; font-weight: 600;">
        ${daysUntil === 1 ? 
          '⏰ The event is tomorrow!' : 
          `⏰ The event is in ${daysUntil} days.`
        }
      </p>
    </div>
    
    ${event.description ? `
      <div style="margin: 20px 0;">
        <h4 style="color: #1f2937;">Event Details:</h4>
        <p style="color: #4b5563; white-space: pre-wrap;">${event.description}</p>
      </div>
    ` : ''}
    
    ${event.registration_url ? `
      <div style="background: #dbeafe; padding: 20px; border-radius: 8px; margin: 20px 0;">
        <p style="color: #1e40af; margin: 0;">
          <strong>Event Page:</strong><br>
          <a href="${event.registration_url}" style="color: #2563eb; text-decoration: underline;">${event.registration_url}</a>
        </p>
      </div>
    ` : ''}
    
    <div style="text-align: center; margin: 30px 0;">
      <a href="https://confplanners.com/events/${event.id}" style="display: inline-block; background: linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%); color: white; padding: 14px 28px; text-decoration: none; border-radius: 8px; font-weight: 600;">View Event Details</a>
    </div>
    
    <p style="color: #6b7280; font-size: 14px; margin-top: 30px; padding-top: 20px; border-top: 1px solid #e5e7eb;">
      We look forward to seeing you there!<br>
      <strong>The ConfPlanners Team</strong>
    </p>
  </div>
  
  <p style="color: #9ca3af; font-size: 12px; text-align: center; margin-top: 20px;">
    You received this email because you registered for this event on ConfPlanners.<br>
    <a href="https://confplanners.com" style="color: #7c3aed;">Visit ConfPlanners</a>
  </p>
</body>
</html>
  `.trim();
};
